import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Checkbox, CircularProgress, FormControlLabel, Grid, MenuItem, Select, Stack, TextField, Typography, Alert, Divider, Switch } from '@mui/material';
import apiClient from '../../api';
import { useAuth } from '../../context/AuthContext';

function DataManagement() {
    const { user, setDynamoPreview } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [companyId, setCompanyId] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [previewSaving, setPreviewSaving] = useState(false);
    const dynamoLive = user?.readStore === 'dynamo';
    const [lastSyncError, setLastSyncError] = useState(null);

    const handleDynamoPreview = async (event) => {
        const on = event.target.checked;
        setPreviewSaving(true);
        setError(null);
        try {
            await setDynamoPreview(on);
            const res = await apiClient.get('/admin/datastore');
            setLastSyncError(res?.data?.data?.lastSyncError || null);
        } catch (e) {
            setError(e?.response?.data?.message || 'Failed to update live database');
        } finally {
            setPreviewSaving(false);
        }
    };

    const [options, setOptions] = useState({
        timeOff: true,
        apparel: true,
        checklists: true,
        drafts: true,
        auditLogs: false // opt-in
    });

    useEffect(() => {
        const run = async () => {
            try {
                setLoadingCompanies(true);
                const res = await apiClient.get('/admin/companies');
                const list = res?.data?.data?.companies || [];
                setCompanies(list);
                try {
                    const storeRes = await apiClient.get('/admin/datastore');
                    setLastSyncError(storeRes?.data?.data?.lastSyncError || null);
                } catch (_) { /* ignore */ }
            } catch (e) {
                setError(e?.response?.data?.message || 'Failed to load companies');
            } finally {
                setLoadingCompanies(false);
            }
        };
        run();
    }, [dynamoLive]);

    const selectedCompany = useMemo(() => companies.find(c => String(c._id) === String(companyId)), [companies, companyId]);
    const canSubmit = companyId && confirmText.trim().toLowerCase() === 'purge' && Object.values(options).some(Boolean) && !submitting;

    const handleToggle = (key) => (e) => setOptions((prev) => ({ ...prev, [key]: e.target.checked }));

    const handlePurge = async () => {
        setSubmitting(true);
        setError(null);
        setResult(null);
        try {
            const res = await apiClient.delete('/admin/purge', { data: { companyId, resources: options } });
            setResult(res?.data?.data?.results || {});
            setConfirmText('');
        } catch (e) {
            setError(e?.response?.data?.message || 'Failed to purge data');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box>
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Data Management</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Permanently delete company-scoped data. This action cannot be undone. Use with extreme caution.
                </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                    <Box>
                        <Typography variant="overline" sx={{ fontWeight: 700 }}>Live database</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
                            New data is always written to Mongo and Dynamo. Flip this to make the whole app read Dynamo. Login stays on Mongo so you can switch back if Dynamo fails.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {previewSaving && <CircularProgress size={18} />}
                        <Typography variant="body2">{dynamoLive ? 'DynamoDB' : 'MongoDB'}</Typography>
                        <Switch
                            checked={dynamoLive}
                            onChange={handleDynamoPreview}
                            disabled={previewSaving}
                            color="warning"
                        />
                    </Stack>
                </Stack>
            </Paper>

            {lastSyncError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Last Dynamo sync error ({lastSyncError.at}): {lastSyncError.model || 'unknown'} {lastSyncError.id || ''} — {lastSyncError.message}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            )}
            {result && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Purge completed{selectedCompany ? ` for ${selectedCompany.name}` : ''}. Deleted counts: {Object.entries(result).map(([k,v]) => `${k}: ${v}`).join(', ') || 'none'}
                </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 700 }}>Company</Typography>
                                <Box sx={{ mt: 1 }}>
                                    {loadingCompanies ? (
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <CircularProgress size={20} />
                                            <Typography variant="body2">Loading companies…</Typography>
                                        </Stack>
                                    ) : (
                                        <Select fullWidth size="small" value={companyId} onChange={(e) => setCompanyId(e.target.value)} displayEmpty>
                                            <MenuItem value="" disabled>Select a company…</MenuItem>
                                            {companies.map((c) => (
                                                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="overline" sx={{ fontWeight: 700 }}>Resources to purge</Typography>
                                <Stack sx={{ mt: 1 }}>
                                    <FormControlLabel control={<Checkbox checked={options.timeOff} onChange={handleToggle('timeOff')} />} label="Time Off Requests" />
                                    <FormControlLabel control={<Checkbox checked={options.apparel} onChange={handleToggle('apparel')} />} label="Apparel Orders" />
                                    <FormControlLabel control={<Checkbox checked={options.checklists} onChange={handleToggle('checklists')} />} label="Weekly Checklists" />
                                    <FormControlLabel control={<Checkbox checked={options.drafts} onChange={handleToggle('drafts')} />} label="Weekly Checklist Drafts" />
                                    <FormControlLabel control={<Checkbox checked={options.auditLogs} onChange={handleToggle('auditLogs')} />} label="Audit Logs (optional)" />
                                </Stack>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={3}>
                            <Alert severity="warning">
                                Type "purge" to confirm. This will permanently delete the selected resources for the chosen company. This action cannot be undone.
                            </Alert>
                            <TextField
                                label="Type 'purge' to confirm"
                                fullWidth
                                size="small"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                            />
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Button variant="contained" color="error" disabled={!canSubmit} onClick={handlePurge}>
                                    {submitting ? 'Purging…' : 'Purge Selected Data'}
                                </Button>
                                {submitting && <CircularProgress size={24} />}
                            </Stack>
                            {selectedCompany && (
                                <Box>
                                    <Typography variant="overline" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>Company ID</Typography>
                                    <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{selectedCompany._id}</Typography>
                                    </Paper>
                                </Box>
                            )}
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
}

export default DataManagement;


